import { useEffect, useState } from 'react';

interface SectionTheme {
  primaryOrb: string;
  secondaryOrb: string;
  accentOrb: string;
  gridOpacity: string;
}

const SECTION_THEMES: Record<string, SectionTheme> = {
  hero: {
    primaryOrb: 'rgba(230, 41, 138, 0.18)', // Brand pink
    secondaryOrb: 'rgba(123, 47, 224, 0.22)', // Purple
    accentOrb: 'rgba(10, 156, 254, 0.15)', // Cyan
    gridOpacity: 'opacity-40',
  },
  trust: {
    primaryOrb: 'rgba(123, 47, 224, 0.22)', // Purple
    secondaryOrb: 'rgba(10, 156, 254, 0.18)', // Cyan
    accentOrb: 'rgba(230, 41, 138, 0.12)', // Brand pink
    gridOpacity: 'opacity-30',
  },
  how: {
    primaryOrb: 'rgba(10, 156, 254, 0.20)', // Cyan
    secondaryOrb: 'rgba(230, 41, 138, 0.16)', // Brand pink
    accentOrb: 'rgba(123, 47, 224, 0.18)', // Purple
    gridOpacity: 'opacity-35',
  },
  services: {
    primaryOrb: 'rgba(123, 47, 224, 0.25)', // Deep Purple
    secondaryOrb: 'rgba(230, 41, 138, 0.20)', // Magenta
    accentOrb: 'rgba(10, 156, 254, 0.16)', // Cyan
    gridOpacity: 'opacity-45',
  },
  safety: {
    primaryOrb: 'rgba(10, 156, 254, 0.22)', // Cyan
    secondaryOrb: 'rgba(21, 136, 90, 0.20)', // Verified green
    accentOrb: 'rgba(123, 47, 224, 0.15)', // Purple
    gridOpacity: 'opacity-50',
  },
  stories: {
    primaryOrb: 'rgba(230, 41, 138, 0.18)', // Brand pink
    secondaryOrb: 'rgba(123, 47, 224, 0.22)', // Purple
    accentOrb: 'rgba(10, 156, 254, 0.14)', // Cyan
    gridOpacity: 'opacity-30',
  },
  partner: {
    primaryOrb: 'rgba(230, 41, 138, 0.25)', // Brand pink
    secondaryOrb: 'rgba(123, 47, 224, 0.22)', // Purple
    accentOrb: 'rgba(10, 156, 254, 0.18)', // Cyan
    gridOpacity: 'opacity-40',
  },
  faq: {
    primaryOrb: 'rgba(123, 47, 224, 0.18)', // Purple
    secondaryOrb: 'rgba(10, 156, 254, 0.16)', // Cyan
    accentOrb: 'rgba(230, 41, 138, 0.12)', // Brand pink
    gridOpacity: 'opacity-30',
  },
};

export default function ScrollAmbientBackground() {
  const [activeSection, setActiveSection] = useState<string>('hero');

  useEffect(() => {
    const sectionIds = ['hero', 'trust', 'how', 'services', 'safety', 'stories', 'partner', 'faq'];

    const handleScroll = () => {
      const scrollPos = window.scrollY + window.innerHeight * 0.35;

      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const id = sectionIds[i];
        if (!id) continue;
        const el = document.getElementById(id);
        if (el) {
          const top = el.offsetTop;
          if (scrollPos >= top) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const theme = SECTION_THEMES[activeSection] ?? SECTION_THEMES.hero!;

  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      {/* ── 1. Geometric Checkers / Grid Texture ── */}
      <div
        className={`absolute inset-0 bg-grid-checkers transition-opacity duration-1000 ease-out ${theme.gridOpacity}`}
      />

      {/* ── 2. Top-Left Primary Glow Orb ── */}
      <div
        className="animate-pulse-glow absolute -top-40 -left-40 h-[600px] w-[600px] rounded-full blur-[120px] transition-all duration-1000 ease-in-out sm:h-[800px] sm:w-[800px]"
        style={{
          background: `radial-gradient(circle, ${theme.primaryOrb} 0%, rgba(0,0,0,0) 70%)`,
          transform: activeSection === 'services' || activeSection === 'safety' ? 'translateY(15%) scale(1.1)' : 'translateY(0%) scale(1)',
        }}
      />

      {/* ── 3. Middle-Right Secondary Glow Orb ── */}
      <div
        className="animate-float absolute top-1/4 -right-40 h-[500px] w-[500px] rounded-full blur-[140px] transition-all duration-1000 ease-in-out sm:h-[750px] sm:w-[750px]"
        style={{
          background: `radial-gradient(circle, ${theme.secondaryOrb} 0%, rgba(0,0,0,0) 70%)`,
          transform: activeSection === 'how' || activeSection === 'partner' ? 'translateY(-10%) scale(1.15)' : 'translateY(0%) scale(1)',
        }}
      />

      {/* ── 4. Bottom Accent Glow Orb ── */}
      <div
        className="absolute -bottom-40 left-1/3 h-[500px] w-[500px] rounded-full blur-[130px] transition-all duration-1000 ease-in-out sm:h-[700px] sm:w-[700px]"
        style={{
          background: `radial-gradient(circle, ${theme.accentOrb} 0%, rgba(0,0,0,0) 70%)`,
        }}
      />

      {/* ── 5. Subtle Vignette / Edge Softener ── */}
      <div className="absolute inset-0 bg-radial from-transparent via-transparent to-black/30" />
    </div>
  );
}
